import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Avatar,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Search, Block, CheckCircle, Star } from "@mui/icons-material";
import { formatDate } from "@nomnom/core";
import type {
  RiderBackgroundCheckStatus,
  RiderProfile,
  VerificationStatus,
} from "../types/firestore";
import {
  buildRiderStats,
  filterRiders,
  subscribeToRiders,
  updateRiderVerification,
  type RiderRow,
} from "../services/ridersService";

type W9Status = "missing" | "submitted" | "approved" | "rejected";
type BackgroundCheckStatus = RiderBackgroundCheckStatus;

const getVehicleIcon = (vehicle: RiderProfile["vehicleType"]): string => {
  switch (vehicle) {
    case "bike":
      return "🚴‍♀️";
    case "e-scooter":
      return "🛵";
    case "foot":
      return "🚶‍♀️";
    default:
      return "❓";
  }
};

export default function Riders() {
  const [riders, setRiders] = useState<RiderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRider, setSelectedRider] = useState<RiderRow | null>(null);
  const [actionDialog, setActionDialog] = useState(false);
  const [actionStatus, setActionStatus] =
    useState<VerificationStatus>("approved");
  const [actionNotes, setActionNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [payoutEligible, setPayoutEligible] = useState(false);
  const [payoutHoldReason, setPayoutHoldReason] = useState("");
  const [safetyHoldActive, setSafetyHoldActive] = useState(false);
  const [safetyHoldReason, setSafetyHoldReason] = useState("");
  const [w9Status, setW9Status] = useState<W9Status>("missing");
  const [backgroundCheckStatus, setBackgroundCheckStatus] =
    useState<BackgroundCheckStatus>("pending");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    return subscribeToRiders(
      (nextRiders) => {
        setRiders(nextRiders);
        setError(null);
        setLoading(false);
      },
      (message) => {
        setError(message);
        setLoading(false);
      },
    );
  }, []);

  const filteredRiders = useMemo(
    () => filterRiders(riders, filter, search),
    [filter, riders, search],
  );

  const stats = useMemo(() => buildRiderStats(riders), [riders]);

  const handleRiderAction = (rider: RiderRow, status: VerificationStatus) => {
    setSelectedRider(rider);
    setActionStatus(status);
    setActionNotes(rider.profile.verificationNotes || "");
    setPayoutEligible(Boolean(rider.profile.payoutEligible));
    setPayoutHoldReason(rider.profile.payoutHoldReason || "");
    setSafetyHoldActive(rider.profile.safetyHoldActive === true);
    setSafetyHoldReason(rider.profile.safetyHoldReason || "");
    setW9Status(rider.profile.w9Status || "missing");
    setBackgroundCheckStatus(rider.profile.backgroundCheckStatus || "pending");
    setActionDialog(true);
    setActionError(null);
  };

  const executeAction = async () => {
    if (!selectedRider) return;

    setActionLoading(true);
    setActionError(null);

    try {
      await updateRiderVerification(
        selectedRider.id,
        actionStatus,
        actionNotes,
        {
          backgroundCheckStatus,
          payoutEligible,
          payoutHoldReason,
          safetyHoldActive,
          safetyHoldReason,
          w9Status,
        },
      );
      setActionDialog(false);
      setSelectedRider(null);
    } catch (err) {
      setActionError(
        err instanceof Error
          ? err.message
          : "Failed to update rider verification.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Riders</Typography>
        <Button variant="contained">Export Riders</Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Riders
              </Typography>
              <Typography variant="h4">{stats.totalRiders}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Verified Riders
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.verifiedRiders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Online Now
              </Typography>
              <Typography variant="h4" color="primary.main">
                {stats.onlineRiders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Rating
              </Typography>
              <Typography variant="h4">
                ⭐ {stats.avgRating.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            placeholder="Search riders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            label="Filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All Riders</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
            <MenuItem value="verified">Verified</MenuItem>
            <MenuItem value="pending">Pending Review</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Riders Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rider</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>Performance</TableCell>
              <TableCell>Earnings</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payout</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 4,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredRiders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" align="center" py={4}>
                    No riders found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRiders.map((rider) => {
                const status = rider.verificationStatus;
                const showApprove = status !== "approved";
                const showReject = status !== "rejected";
                const documentsComplete =
                  Boolean(rider.profile.documents?.ucsdIdUrl) &&
                  Boolean(rider.profile.documents?.selfieUrl);

                return (
                  <TableRow key={rider.id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Avatar sx={{ mr: 2 }}>
                          {rider.displayName
                            .split(" ")
                            .map((name) => name[0])
                            .join("")}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {rider.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {rider.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Joined {formatDate(rider.profile.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography sx={{ mr: 1 }}>
                          {getVehicleIcon(rider.profile.vehicleType)}
                        </Typography>
                        <Typography variant="body2">
                          {rider.profile.vehicleType.replace("-", " ")}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 0.5,
                          }}
                        >
                          <Star
                            sx={{ mr: 0.5, color: "gold" }}
                            fontSize="small"
                          />
                          <Typography variant="body2">
                            {rider.profile.ratingAvg.toFixed(1)}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {rider.profile.totalDeliveries} deliveries
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rider.profile.stats.acceptanceRate}% acceptance
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        ${rider.profile.totalEarnings.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Lifetime
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={rider.profile.online ? "Online" : "Offline"}
                          color={rider.profile.online ? "success" : "default"}
                          size="small"
                        />
                        {status === "approved" && (
                          <Chip
                            label="Verified"
                            color="success"
                            size="small"
                            icon={<CheckCircle />}
                          />
                        )}
                        {status === "pending" && (
                          <Chip label="Pending" color="warning" size="small" />
                        )}
                        {status === "rejected" && (
                          <Chip
                            label="Rejected"
                            color="error"
                            size="small"
                            icon={<Block fontSize="small" />}
                          />
                        )}
                        <Chip
                          label={
                            documentsComplete ? "Docs Complete" : "Docs Missing"
                          }
                          color={documentsComplete ? "success" : "warning"}
                          size="small"
                        />
                        <Chip
                          label={`Background ${rider.profile.backgroundCheckStatus ?? "pending"}`}
                          color={
                            rider.profile.backgroundCheckStatus === "cleared"
                              ? "success"
                              : rider.profile.backgroundCheckStatus === "failed"
                                ? "error"
                                : "warning"
                          }
                          size="small"
                        />
                        {rider.profile.safetyHoldActive ? (
                          <Chip
                            label="Safety Hold"
                            color="error"
                            size="small"
                          />
                        ) : null}
                        {rider.profile.requiredOfflineUntil ? (
                          <Chip
                            label="Mandatory Rest"
                            color="warning"
                            size="small"
                          />
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={
                            rider.profile.payoutEligible
                              ? "Payout Ready"
                              : "Payout Hold"
                          }
                          color={
                            rider.profile.payoutEligible ? "success" : "warning"
                          }
                          size="small"
                        />
                        <Chip
                          label={`W-9 ${rider.profile.w9Status ?? "missing"}`}
                          color={
                            rider.profile.w9Status === "approved"
                              ? "success"
                              : rider.profile.w9Status === "submitted"
                                ? "info"
                                : rider.profile.w9Status === "rejected"
                                  ? "error"
                                  : "default"
                          }
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {rider.profile.payoutMethod === "mercury_ach"
                            ? "Manual Mercury ACH"
                            : rider.profile.venmoHandle
                              ? `@${rider.profile.venmoHandle}`
                              : "Payout setup incomplete"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {showApprove && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleRiderAction(rider, "approved")}
                          >
                            Approve
                          </Button>
                        )}
                        {showReject && (
                          <Tooltip title="Reject Rider">
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() =>
                                handleRiderAction(rider, "rejected")
                              }
                            >
                              Reject
                            </Button>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Dialog */}
      <Dialog open={actionDialog} onClose={() => setActionDialog(false)}>
        <DialogTitle>Rider Verification</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {selectedRider?.displayName} ({selectedRider?.email})
          </Typography>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current Status:{" "}
              {selectedRider?.verificationStatus
                ? selectedRider.verificationStatus
                : "pending"}
            </Typography>

            <TextField
              select
              fullWidth
              label="Decision"
              value={actionStatus}
              onChange={(e) =>
                setActionStatus(e.target.value as VerificationStatus)
              }
              sx={{ mt: 2 }}
            >
              <MenuItem value="approved">Approve</MenuItem>
              <MenuItem value="rejected">Reject</MenuItem>
              <MenuItem value="pending">Mark Pending</MenuItem>
            </TextField>

            <TextField
              label="Admin Notes"
              multiline
              rows={3}
              placeholder="Add verification notes or rejection reason..."
              fullWidth
              sx={{ mt: 2 }}
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
            />

            <TextField
              select
              fullWidth
              label="Background Check"
              value={backgroundCheckStatus}
              onChange={(e) =>
                setBackgroundCheckStatus(
                  e.target.value as BackgroundCheckStatus,
                )
              }
              sx={{ mt: 2 }}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="cleared">Cleared</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label="W-9 Status"
              value={w9Status}
              onChange={(e) => setW9Status(e.target.value as W9Status)}
              sx={{ mt: 2 }}
            >
              <MenuItem value="missing">Missing</MenuItem>
              <MenuItem value="submitted">Submitted</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label="Payout Eligibility"
              value={payoutEligible ? "eligible" : "hold"}
              onChange={(e) => setPayoutEligible(e.target.value === "eligible")}
              sx={{ mt: 2 }}
            >
              <MenuItem value="eligible">Eligible</MenuItem>
              <MenuItem value="hold">On Hold</MenuItem>
            </TextField>

            <TextField
              select
              fullWidth
              label="Safety Hold"
              value={safetyHoldActive ? "active" : "clear"}
              onChange={(e) => setSafetyHoldActive(e.target.value === "active")}
              sx={{ mt: 2 }}
            >
              <MenuItem value="clear">Clear</MenuItem>
              <MenuItem value="active">Active</MenuItem>
            </TextField>

            {safetyHoldActive && (
              <TextField
                label="Safety Hold Reason"
                multiline
                rows={2}
                placeholder="Explain the zero-tolerance or safety hold reason..."
                fullWidth
                sx={{ mt: 2 }}
                value={safetyHoldReason}
                onChange={(e) => setSafetyHoldReason(e.target.value)}
              />
            )}

            {!payoutEligible && (
              <TextField
                label="Payout Hold Reason"
                multiline
                rows={2}
                placeholder="Explain what the rider must fix before payout..."
                fullWidth
                sx={{ mt: 2 }}
                value={payoutHoldReason}
                onChange={(e) => setPayoutHoldReason(e.target.value)}
              />
            )}

            {selectedRider && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Documents:
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label={
                      selectedRider.profile.documents?.ucsdIdUrl
                        ? "UCSD ID Uploaded"
                        : "UCSD ID Missing"
                    }
                    color={
                      selectedRider.profile.documents?.ucsdIdUrl
                        ? "success"
                        : "warning"
                    }
                    size="small"
                  />
                  <Chip
                    label={
                      selectedRider.profile.documents?.selfieUrl
                        ? "Selfie Uploaded"
                        : "Selfie Missing"
                    }
                    color={
                      selectedRider.profile.documents?.selfieUrl
                        ? "success"
                        : "warning"
                    }
                    size="small"
                  />
                </Box>
              </Box>
            )}

            {actionError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {actionError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setActionDialog(false)}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={executeAction}
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? "Updating..." : "Update Rider"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
